<template>
  <div class="container-fluid">
    <div class="row">
      <TableProductPlan @download="download_csv"/>
    </div>
  </div>
</template>

<script setup>
import { computed , defineAsyncComponent , ref, onMounted} from 'vue'
import { useRoute } from 'vue-router'

const TableProductPlan = defineAsyncComponent(() =>
  import('@/components/theme/product-planner/TableProductPlan.vue')
)

const route = useRoute()

// Check if the route has :id param
const isViewMode = computed(() => !!route.params.id)

import { directus } from '@/services/directus';
import { readItems } from '@directus/sdk';
import axios from 'axios';

const pdkmake_ip = process.env.VUE_APP_PDFMAKE_URL && process.env.VUE_APP_PDFMAKE_URL.startsWith("http")
    ? process.env.VUE_APP_PDFMAKE_URL
    : 'http://localhost:3001';

const listProduct = ref([])

function transformData(data) {
    function getReservedFor(plans, productCode) {
        // เรียงตาม created_at ก่อน map (ไม่มีวันติดตั้งอยู่ล่างสุด)
        return plans
            .slice()
            .sort((a, b) => {
                if (!a.created_at && !b.created_at) return 0;
                if (!a.created_at) return 1;
                if (!b.created_at) return -1;
                return new Date(a.created_at) - new Date(b.created_at);
            })
            .map(plan => {
                const qty = plan.installation_plan_products
                    .filter(p => p.product_code === productCode)
                    .reduce((sum, p) => sum + (p.quantity || 0), 0);
                const name = `${plan.store_name || ''} ${plan.branch_name || ''}`;
                return `${name} (${qty})`;
            }).join('\n');
    }
    const result = [];

    Object.values(data).flat().forEach(product => {
        if (product.installation_plans && product.installation_plans.length > 0) {
            // เรียง installation_plans: มีวันติดตั้งก่อน, ไม่มีวันติดตั้งอยู่ล่างสุด
            const sortedPlans = product.installation_plans
                .slice()
                .sort((a, b) => {
                    if (!a.created_at && !b.created_at) return 0;
                    if (!a.created_at) return 1;
                    if (!b.created_at) return -1;
                    return new Date(a.created_at) - new Date(b.created_at);
                });
            let remain = product.quality;
            let shortageBranches = [];
            for (const plan of sortedPlans) {
                const qty = (plan.installation_plan_products || [])
                    .filter(p => p.product_code === product.product_code)
                    .reduce((sum, p) => sum + (p.quantity || 0), 0);
                if (remain < qty) {
                    const name = `${plan.store_name || ''} ${plan.branch_name || ''}`;
                    shortageBranches.push(`${name} (${qty - remain > 0 ? qty - remain : qty})`);
                    remain = 0;
                } else {
                    remain -= qty;
                }
            }

            result.push({
                "อุปกรณ์": product.device,
                "ยี่ห้อ": product.brand,
                "รุ่น": product.model,
                "จำนวนนับสต๊อก ณ ปัจจุบัน": product.quality,
                "ต้องใช้": product.planner_count,
                "จำนวนคงเหลือ": product.quality - product.planner_count,
                "ปัจจุบันจองให้ใคร": getReservedFor(sortedPlans, product.product_code),
                "ขาดแคลนที่สาขา": shortageBranches.length > 0 ? shortageBranches.join('\n') : '',
            });
        }
    });
    return result;
}

async function download_csv() {
    const data = transformData(listProduct.value);
    const planData = transformPlanData(listProduct.value);
    const now = new Date();
    const pad = n => n.toString().padStart(2, '0');
    const fileName = `product-plan_${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const payload = {
        fileName: fileName,
        data: data,
        planData: planData
    };

    const response = await axios.post(`${pdkmake_ip}/excel/product_plan`, payload, {
        responseType: 'blob',
        headers: {
            'Content-Type': 'application/json'
        }
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

async function fetchProductList() {
    // 1. ดึงสินค้า
    const products = await directus.request(
        readItems("product", {
            fields: ["product_name", "device", "brand", "model", "min_threshold", "delivery_duration", "product_code", "count", "id", "sell"],
            limit: -1,
        })
    );

    const productCodes = products
        .map(p => p.product_code)
        .filter(code => !!code); // กรอง null/undefined

    // 2. ดึง stock ทีเดียว โดยกรองเฉพาะ product_code_office_design ที่เกี่ยวข้อง
    let stockItems = [];
    if (productCodes.length > 0) {
        stockItems = await directus.request(
            readItems("stock", {
                fields: ["product_code_office_design","stock_id"],
                limit: -1,
                filter: {
                    product_code_office_design: {
                        _in: productCodes
                    },
                    status: {
                        _in: ['รอตรวจสอบอุปกรณ์','พร้อมใช้งาน','รอเช็คก่อนส่ง']
                    },
                    device_status: {
                        _eq: 'สินค้าขาย'
                    }
                }
            })
        );
    }

    const product_planner = productCodes.length > 0 ? await directus.request(
        readItems("installation_plan_products", {
            fields: [
                "product_code",
                "quantity",
                "installation_plan.id",
                "installation_plan.company_name",
                "installation_plan.store_name",
                "installation_plan.branch_code",
                "installation_plan.branch_name",
                "installation_plan.created_at",
                "installation_plan.status",
                "installation_plan.installation_plan_products.*"
            ],
            limit: -1,
            filter: {
                product_code: {
                    _in: productCodes
                },
                installation_plan: {
                    status: {
                        _in: ['pending']
                    }
                }
            }
        })
    ) : [];

    // 3. นับจำนวน stock ต่อ product_code
    const stockCountMap = {};
    const orderCountMap = {}; 

    for (const stock of stockItems) {
        const code = stock.product_code_office_design;
        stockCountMap[code] = (stockCountMap[code] || 0) + 1;
        if (stock.stock_id) {
            orderCountMap[code] = (orderCountMap[code] || 0) + 1;
        }
    }

    // 4. ใส่ค่า qulitty ให้แต่ละสินค้า และรวม installation_plan
    const groupedByDevice = {};
    for (const item of products) {
        const code = item.product_code;

        item.quality = stockCountMap[code] || 0;
        item.in_order = orderCountMap[code] || 0;
        item.planner_count = 0; // default เป็น 0

        // คำนวณจำนวนจาก installation_plan_products
        const plannerItems = product_planner.filter(p => p.product_code === code);
        const totalPlannerQuantity = plannerItems.reduce((sum, p) => sum + (p.quantity || 0), 0);

        item.planner_count = totalPlannerQuantity;

        // รวม installation_plan.* ทั้งหมดที่เกี่ยวข้องกับ product_code นี้
        item.installation_plans = plannerItems.map(p => p.installation_plan);

        if (!groupedByDevice[item.device]) {
            groupedByDevice[item.device] = [];
        }
        groupedByDevice[item.device].push(item);
    }

    listProduct.value = groupedByDevice;   
}

function transformPlanData(data) {
    const planMap = new Map();

    Object.values(data).flat().forEach(product => {
        if (product.installation_plans && product.installation_plans.length > 0) {
            product.installation_plans.forEach(plan => {
                const planKey = plan.id || [
                    plan.created_at,
                    plan.store_name,
                    plan.branch_name
                ].join('|');

                const qty = (plan.installation_plan_products || [])
                    .filter(p => p.product_code === product.product_code)
                    .reduce((sum, p) => sum + (p.quantity || 0), 0);

                if (!planMap.has(planKey)) {
                    planMap.set(planKey, {
                        items: [],
                        created_at: plan.created_at,
                        store_name: plan.store_name,
                        branch_name: plan.branch_name
                    });
                }
                planMap.get(planKey).items.push(
                    `${product.device} ${product.brand} ${product.model}(${qty})`
                );
            });
        }
    });

    const result = [];
    for (const [planKey, value] of planMap.entries()) {
        result.push({
            "แพลนติดตั้ง": value.created_at ? new Date(value.created_at).toLocaleDateString('th-TH') : "",
            "ร้านค้า": value.store_name || "",
            "สาขา": value.branch_name || "",
            "อุปกรณ์": value.items.join('\n')
        });
    }
    // เรียงลำดับจากวันแพลนติดตั้ง (เก่า -> ใหม่)
    result.sort((a, b) => {
        const [da, ma, ya] = a["แพลนติดตั้ง"].split('/');
        const [db, mb, yb] = b["แพลนติดตั้ง"].split('/');
        const dateA = new Date(+ya, +ma - 1, +da);
        const dateB = new Date(+yb, +mb - 1, +db);
        return dateA - dateB;
    });
    return result;
}

onMounted(() => {
    fetchProductList()
})
</script>
